'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, FileText, Calendar, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { journalEntries } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { journalEntryStatusConfig, type JournalEntryStatus } from '../types';
import type { JournalEntry } from '../types';

// 분개 입력 모달
function JournalEntryAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ accountCode: '', accountName: '', debit: 0, credit: 0, remarks: '' }]);

  const addItem = () => setItems([...items, { accountCode: '', accountName: '', debit: 0, credit: 0, remarks: '' }]);
  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="분개 입력"
      description="새로운 분개 전표를 작성합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline" onClick={onClose}>임시 저장</Button>
          <Button onClick={onClose}>전기</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <FormField label="전표번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="전표일자" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="전표유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="일반">일반</option>
              <option value="매입">매입</option>
              <option value="매출">매출</option>
              <option value="결제">결제</option>
              <option value="급여">급여</option>
              <option value="감가상각">감가상각</option>
            </FormSelect>
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">분개 항목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />항목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">계정과목</th>
                  <th className="text-right p-3 font-medium w-32">차변</th>
                  <th className="text-right p-3 font-medium w-32">대변</th>
                  <th className="text-left p-3 font-medium">적요</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">계정과목 선택</option>
                        <option value="1010">현금</option>
                        <option value="1020">보통예금</option>
                        <option value="1110">매출채권</option>
                        <option value="2010">매입채무</option>
                        <option value="4010">매출</option>
                        <option value="5010">매입원가</option>
                        <option value="5020">급여</option>
                        <option value="5030">임차료</option>
                      </FormSelect>
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput placeholder="적요" />
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td className="p-3 font-medium text-right">합계</td>
                  <td className="p-3 text-right font-bold">₩0</td>
                  <td className="p-3 text-right font-bold">₩0</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            * 차변 합계와 대변 합계가 일치해야 전기할 수 있습니다.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="참조번호">
            <FormInput placeholder="관련 문서번호" />
          </FormField>
          <FormField label="첨부파일">
            <FormInput type="file" />
          </FormField>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="추가 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function JournalEntryListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return journalEntries.filter((entry) => {
      // 검색 필터
      const matchesSearch =
        entry.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.entryType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entry.remarks && entry.remarks.toLowerCase().includes(searchQuery.toLowerCase()));

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || entry.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  const columns = [
    {
      key: 'code',
      label: '전표번호',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'entryDate',
      label: '일자',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm whitespace-nowrap">
          <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span>{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'entryType',
      label: '유형',
      width: '7%',
      render: (value: unknown) => (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="text-xs">{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'remarks',
      label: '적요',
      width: '20%',
      render: (value: unknown, row: JournalEntry) => (
        <div className="min-w-0">
          <div className="text-sm truncate">{value ? String(value) : '-'}</div>
          {row.reference && (
            <div className="text-xs text-muted-foreground truncate">
              참조: {row.reference}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'items',
      label: '항목',
      width: '6%',
      render: (value: unknown) => {
        const items = value as JournalEntry['items'];
        return <span className="text-sm whitespace-nowrap">{items.length}건</span>;
      },
    },
    {
      key: 'totalDebit',
      label: '차변',
      width: '13%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="font-mono text-sm whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'totalCredit',
      label: '대변',
      width: '13%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="font-mono text-sm whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as JournalEntryStatus;
        const config = journalEntryStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, { count: number; amount: number }> = {};
    journalEntries.forEach((entry) => {
      if (!stats[entry.status]) {
        stats[entry.status] = { count: 0, amount: 0 };
      }
      stats[entry.status].count++;
      stats[entry.status].amount += entry.totalDebit;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalEntries = journalEntries.length;
  const totalAmount = journalEntries.reduce((sum, e) => sum + e.totalDebit, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="분개장"
        description="분개 내역을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            분개 입력
          </button>
        }
      />

      {/* 상태별 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(journalEntryStatusConfig).map(([status, config]) => {
          const stat = statusStats[status] || { count: 0, amount: 0 };
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
              className={`p-4 rounded-lg border text-left transition-colors ${
                selectedStatus === status
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:bg-accent/50'
              }`}
            >
              <div className="text-sm text-muted-foreground">{config.label}</div>
              <div className="text-2xl font-bold mt-1">{stat.count}건</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatCurrency(stat.amount)}
              </div>
            </button>
          );
        })}
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 분개</div>
          <div className="text-2xl font-bold mt-1">{totalEntries}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="전표번호, 유형, 적요 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(journalEntryStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 분개 입력 모달 */}
      <JournalEntryAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
